import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Area, Botao, Dados, Escolha, ListaTexto, Secao } from "@/components/sistema";
import type { OpcaoEscolha } from "@/components/sistema";

export type ReviewSource = "popup" | "early" | "profile";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after successful submission */
  onSubmitted?: () => void;
  /** If true, show "never show again" option (for daily popup) */
  showNeverAgain?: boolean;
  onNeverAgain?: () => void;
  source?: ReviewSource;
}

type Lang = "en" | "es" | "de";

/** New survey answer shape, stored as jsonb in user_reviews.reasons */
type SurveyAnswers = {
  problems: string;
  expected: string;
  features: string;
  ai_issues: string;
};

const SURVEY_QUESTIONS: { key: keyof SurveyAnswers; en: string; es: string; de: string }[] = [
  {
    key: "problems",
    en: "Did you experience any problems using the app?",
    es: "¿Tuviste algún problema usando el app?",
    de: "Hattest du Probleme bei der Nutzung der App?",
  },
  {
    key: "expected",
    en: "What did you expect to find in the app that you didn't?",
    es: "¿Qué esperabas encontrar en el app y no encontraste?",
    de: "Was hast du im App erwartet, aber nicht gefunden?",
  },
  {
    key: "features",
    en: "What features do you consider important in an app like this?",
    es: "¿Qué funciones consideras importantes en un app como este?",
    de: "Welche Funktionen hältst du in einer App wie dieser für wichtig?",
  },
  {
    key: "ai_issues",
    en: "Was there any AI feature that didn't work as expected?",
    es: "¿Hubo alguna función de IA que no funcionó como esperabas?",
    de: "Gab es eine KI-Funktion, die nicht wie erwartet funktioniert hat?",
  },
];

// Legacy chip labels — only used to display OLD reviews (array-of-keys format)
// in the "already reviewed" summary. New submissions never write this shape.
const LEGACY_REASON_LABELS: Record<string, string> = {
  receitas_incompletas: "Incomplete recipes",
  pouco_conteudo: "Not enough content",
  lingua_incorreta: "Wrong language",
  dificil_de_usar: "Hard to navigate",
  esperava_diferente: "Expected something different",
  problema_tecnico: "Technical problem",
  preco_alto: "Price too high",
  outro: "Other",
};

type Step = "stars" | "survey" | "done";

type Nota = "1" | "2" | "3" | "4" | "5";
const NOTAS: Nota[] = ["1", "2", "3", "4", "5"];

type ExistingReview = {
  id: string;
  stars: number;
  reasons: string[] | SurveyAnswers | null;
  comment: string | null;
};

const isSurveyAnswers = (r: ExistingReview["reasons"]): r is SurveyAnswers =>
  !!r && typeof r === "object" && !Array.isArray(r);

const emptyAnswers = (): SurveyAnswers => ({ problems: "", expected: "", features: "", ai_issues: "" });

/**
 * Avaliação do app: nota de 1 a 5 e quatro perguntas abertas.
 *
 * A escala é a mesma de antes — 1 a 5, gravada em `user_reviews.stars`. O que
 * mudou é como ela é oferecida: eram cinco estrelas de 40px numa fileira, com
 * a cor âmbar e o rótulo da nota aparecendo só no hover. Numa tela de 390px
 * isso dá cinco alvos de ~52px encostados, e no celular não existe hover: quem
 * tem 70 anos errava a estrela e nunca lia "Regular" ou "Bueno".
 *
 * Agora é `Escolha`: uma nota por linha, 72px de altura, com a palavra escrita
 * ("Muy malo" … "Excelente") vinda das chaves `rating.star_1..5` que já
 * existiam nos três idiomas. A cor âmbar saiu — o app tem uma cor de marca só.
 *
 * As quatro perguntas eram `textarea` de duas linhas com texto de 14px;
 * viraram `Area`, com o enunciado como rótulo fora e acima.
 *
 * Lógica intocada: mesma busca do review existente com o temporizador de
 * segurança de 5s, mesmo insert/update, mesmo payload (`reasons` como jsonb e
 * `comment` = resposta de "problemas"), mesmos `source`/`language`/`market`/
 * `context`, mesma exigência de responder as quatro para enviar.
 */
export default function UserReviewModal({
  open, onClose, onSubmitted, showNeverAgain, onNeverAgain, source = "popup",
}: Props) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { language, market } = useLocale();
  const lang = language as Lang;

  const tr = (en: string, es: string, de: string) => (lang === "en" ? en : lang === "de" ? de : es);

  const [step, setStep] = useState<Step>("stars");
  const [stars, setStars] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>(emptyAnswers());
  const [saving, setSaving] = useState(false);

  // Existing review state
  const [existing, setExisting] = useState<ExistingReview | null | undefined>(undefined); // undefined = loading
  const [editing, setEditing] = useState(false);

  // Load existing review on open
  useEffect(() => {
    if (!open || !user?.id) return;

    let cancelled = false;

    // Safety timeout: if the query hangs for any reason, close the modal
    // instead of leaving the user trapped behind a blurry unresponsive overlay.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        onClose();
      }
    }, 5000);

    (async () => {
      try {
        const { data: appUser } = await supabase
          .from("app_users")
          .select("id")
          .eq("id", user.id)
          .maybeSingle();

        // If user not found in app_users, exit gracefully — do NOT leave
        // `existing` as undefined (which would trap the user in a blurry
        // fullscreen spinner with no way to close it).
        if (!appUser?.id) {
          if (!cancelled) { cancelled = true; clearTimeout(safetyTimer); onClose(); }
          return;
        }

        if (cancelled) return;

        const { data } = await supabase
          .from("user_reviews" as any)
          .select("id, stars, reasons, comment")
          .eq("user_id", appUser.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!cancelled) {
          clearTimeout(safetyTimer);
          setExisting((data as unknown as ExistingReview | null) ?? null);
        }
      } catch {
        // On any DB error, close the modal instead of trapping the user
        if (!cancelled) { cancelled = true; clearTimeout(safetyTimer); onClose(); }
      }
    })();

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, [open, user?.id, onClose]);

  const resetAndClose = () => {
    setStep("stars");
    setStars(0);
    setAnswers(emptyAnswers());
    setEditing(false);
    setExisting(undefined);
    onClose();
  };

  const startEdit = () => {
    if (!existing) return;
    setStars(existing.stars);
    setAnswers(isSurveyAnswers(existing.reasons) ? existing.reasons : emptyAnswers());
    setEditing(true);
    setStep("survey");
  };

  const handleStarSelect = (n: number) => {
    setStars(n);
    setTimeout(() => setStep("survey"), 200);
  };

  const setAnswer = (key: keyof SurveyAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const allAnswered = SURVEY_QUESTIONS.every((q) => answers[q.key].trim().length > 0);

  const handleSubmit = async () => {
    if (!user?.id || stars === 0 || !allAnswered) return;
    setSaving(true);
    try {
      const { data: appUser } = await supabase
        .from("app_users")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (!appUser?.id) throw new Error("User not found");

      const payload = {
        stars,
        reasons: answers,
        comment: answers.problems.trim() || null,
      };

      if (editing && existing?.id) {
        // UPDATE existing review
        const { error } = await supabase
          .from("user_reviews" as any)
          .update(payload as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        // INSERT new review
        const { error } = await supabase.from("user_reviews" as any).insert({
          user_id: appUser.id,
          source,
          language: i18n.language,
          market,
          context: window.location.pathname,
          ...payload,
        } as any);
        if (error) throw error;
      }

      setStep("done");
      onSubmitted?.();
    } catch (err: any) {
      console.error("[UserReviewModal] Failed to save review:", err);
      toast({ title: tr("Something went wrong", "Algo salió mal", "Etwas ist schiefgelaufen"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const rotuloNota = (n: number) => t(`rating.star_${n}`);

  const OPCOES_NOTA: OpcaoEscolha<Nota>[] = NOTAS.map((n) => ({
    valor: n,
    rotulo: rotuloNota(Number(n)),
  }));

  /** Caixa do modal. O X de fechar tem alvo próprio de 56px. */
  const Caixa = ({
    children,
    fechavel = true,
  }: {
    children: React.ReactNode;
    fechavel?: boolean;
  }) => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" style={{ padding: 12 }}>
      <div
        className="relative w-full overflow-y-auto rounded-2xl border border-border bg-background"
        style={{ maxWidth: 420, maxHeight: "88vh" }}
      >
        {fechavel && (
          <button
            type="button"
            onClick={resetAndClose}
            aria-label={t("common.close")}
            className="absolute right-1 top-1 z-10 flex items-center justify-center rounded-full"
            style={{ width: "var(--alvo-min)", height: "var(--alvo-min)" }}
          >
            <X className="h-6 w-6" strokeWidth={2.5} style={{ color: "var(--tinta-apoio)" }} />
          </button>
        )}
        {children}
      </div>
    </div>
  );

  // Loading existing review
  if (existing === undefined) {
    return (
      <Caixa>
        <div className="flex items-center justify-center" style={{ padding: "56px 0" }}>
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-foreground" />
        </div>
      </Caixa>
    );
  }

  // Already reviewed — show summary (unless editing)
  if (existing && !editing && step === "stars") {
    const legacyReasons = Array.isArray(existing.reasons) ? existing.reasons : [];
    const surveyReasons = isSurveyAnswers(existing.reasons) ? existing.reasons : null;

    return (
      <Caixa>
        <div style={{ padding: "28px 0 24px" }}>
          <div style={{ padding: "0 var(--gutter)" }}>
            <p className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
              {tr("You already reviewed!", "¡Ya has opinado!", "Du hast bereits bewertet!")}
            </p>
            <p
              className="leading-snug"
              style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)", paddingTop: 8 }}
            >
              {tr("Here's what you said:", "Esto es lo que dijiste:", "Das hast du gesagt:")}
            </p>
          </div>

          <div style={{ paddingTop: 20 }}>
            <Dados
              itens={[
                {
                  rotulo: t("rating.main_question"),
                  valor: `${existing.stars}/5 · ${rotuloNota(existing.stars)}`,
                },
              ]}
            />
          </div>

          {surveyReasons && (
            <>
              {SURVEY_QUESTIONS.map((q) =>
                surveyReasons[q.key] ? (
                  <div key={q.key} style={{ padding: "20px var(--gutter) 0" }}>
                    <p
                      className="font-semibold leading-snug"
                      style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}
                    >
                      {tr(q.en, q.es, q.de)}
                    </p>
                    <p className="leading-relaxed" style={{ fontSize: "var(--t-corpo)", paddingTop: 6 }}>
                      {surveyReasons[q.key]}
                    </p>
                  </div>
                ) : null,
              )}
            </>
          )}

          {legacyReasons.length > 0 && (
            <div style={{ paddingTop: 20 }}>
              <ListaTexto itens={legacyReasons.map((r) => LEGACY_REASON_LABELS[r] || r)} />
            </div>
          )}

          {!surveyReasons && existing.comment && (
            <p
              className="leading-relaxed"
              style={{
                padding: "20px var(--gutter) 0",
                fontSize: "var(--t-corpo)",
                color: "var(--tinta-apoio)",
              }}
            >
              {existing.comment}
            </p>
          )}

          <div style={{ padding: "24px var(--gutter) 0" }}>
            <Botao
              rotulo={t("rating.edit_btn")}
              variante="secundaria"
              onClick={startEdit}
            />
          </div>
        </div>
      </Caixa>
    );
  }

  return (
    <Caixa fechavel={step !== "done"}>
      {/* ── Passo: nota ── */}
      {step === "stars" && (
        <div style={{ padding: "28px 0 24px" }}>
          <div style={{ padding: "0 var(--gutter)" }}>
            <p className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
              {t("rating.title")}
            </p>
            <p
              className="leading-snug"
              style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)", paddingTop: 8 }}
            >
              {t("rating.subtitle")}
            </p>
          </div>

          <Secao>{t("rating.main_question")}</Secao>
          <Escolha
            opcoes={OPCOES_NOTA}
            valor={String(stars) as Nota}
            onEscolher={(v) => handleStarSelect(Number(v))}
          />

          {showNeverAgain && (
            <div style={{ padding: "20px var(--gutter) 0" }}>
              <Botao
                rotulo={t("daily_feedback.never_show")}
                variante="secundaria"
                onClick={onNeverAgain}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Passo: perguntas (uma rolagem, quatro abertas) ── */}
      {step === "survey" && (
        <div style={{ padding: "28px 0 24px" }}>
          <div style={{ padding: "0 var(--gutter)" }}>
            <p className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
              {tr("A few quick questions", "Algunas preguntas rápidas", "Ein paar kurze Fragen")}
            </p>
            <p
              className="leading-snug"
              style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)", paddingTop: 8 }}
            >
              {`${stars}/5 · ${rotuloNota(stars)}`}
            </p>
          </div>

          <div style={{ paddingTop: 20, display: "grid", gap: 20 }}>
            {SURVEY_QUESTIONS.map((q) => (
              <Area
                key={q.key}
                rotulo={tr(q.en, q.es, q.de)}
                valor={answers[q.key]}
                onMudar={(v) => setAnswer(q.key, v)}
                placeholder={tr("Your answer...", "Tu respuesta...", "Deine Antwort...")}
                linhas={3}
                maximo={500}
              />
            ))}
          </div>

          <div style={{ padding: "24px var(--gutter) 0", display: "grid", gap: 12 }}>
            <Botao
              rotulo={
                saving
                  ? t("common.saving")
                  : editing
                    ? t("rating.save_changes")
                    : t("rating.send")
              }
              desabilitado={saving || !allAnswered}
              onClick={handleSubmit}
            />
            <Botao
              rotulo={tr("Leave for later", "Dejar para después", "Später")}
              variante="secundaria"
              onClick={resetAndClose}
            />
          </div>
        </div>
      )}

      {/* ── Passo: fim ── */}
      {step === "done" && (
        <div style={{ padding: "28px 0 24px" }}>
          <div style={{ padding: "0 var(--gutter)" }}>
            <p className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
              {stars >= 4
                ? tr("Thanks so much!", "¡Muchas gracias!", "Vielen Dank!")
                : tr("Thanks for the honesty!", "¡Gracias por la sinceridad!", "Danke für deine Ehrlichkeit!")}
            </p>
            <p
              className="leading-relaxed"
              style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)", paddingTop: 8 }}
            >
              {stars >= 4
                ? tr("Your support means a lot to us.", "Tu apoyo significa mucho para nosotros.", "Deine Unterstützung bedeutet uns viel.")
                : tr("We're working to get better.", "Estamos trabajando para mejorar.", "Wir arbeiten daran, besser zu werden.")}
            </p>
          </div>
          <div style={{ padding: "24px var(--gutter) 0" }}>
            <Botao rotulo={t("common.close")} onClick={resetAndClose} />
          </div>
        </div>
      )}
    </Caixa>
  );
}
